const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 992,
};

const defaultsOptions = {
  numLines: 3,
  classNames: {
    main: 'clamp-expandable',
    body: 'clamp-expandable__body',
    item: 'clamp-expandable__item',
    trigger: 'clamp-expandable__trigger',
    modifierText: 'clamp-expandable--text',
    modifierTruncated: 'clamp-expandable--truncated',
    expanded: 'expanded',
  },
};

export default class ClampExpandable {
  #currentBreakpoint = null;
  clamping = true;
  expanded = false;
  lineHeight = null;

  constructor(el) {
    this.el = el;
    this.options = {
      numLines: this.#getNumLinesFromClassModifier(),
      classNames: defaultsOptions.classNames,
    };

    this.body = this.el.querySelector(`.${this.options.classNames.body}`);
    this.triggers = this.el.querySelectorAll(`.${this.options.classNames.trigger}`);
    this.expanded = el.classList.contains(this.options.classNames.expanded);

    this.#calcLineHeight();

    if (!this.expanded) {
      this.setMaxHeight(this.lineHeight * this.options.numLines);
    }

    this.triggers.forEach((trg) => {
      trg.addEventListener('click', (evt) => {
        this.#clickTrigger(evt);
      });
    });

    this.#checkBreakPoint();
    window.addEventListener('resize', () => {
      this.#checkBreakPoint();
    });

    const widthObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        this.checkClamping();
      });
    });
    this.checkClamping();
    widthObserver.observe(this.body);
  }

  expand(animation = true) {
    if (this.expanded && !this.clamping) return;

    this.expanded = true;
    if (animation) {
      this.setMaxHeight(this.lineHeight * this.options.numLines);
      this.el.classList.add(this.options.classNames.expanded);
      this.setMaxHeight(this.body.scrollHeight);
      this.body.addEventListener(
        'transitionend',
        () => {
          this.setMaxHeight(null);
        },
        { once: true }
      );
    } else {
      this.setMaxHeight(null);
      this.el.classList.add(this.options.classNames.expanded);
    }
  }

  collapse(animation = true) {
    if (!this.expanded && !this.clamping) return;
    this.expanded = false;
    if (animation) {
      this.setMaxHeight(this.body.scrollHeight);
      this.#calcLineHeight();
      this.setMaxHeight(this.lineHeight * this.options.numLines);
      this.body.addEventListener(
        'transitionend',
        () => {
          this.el.classList.remove(this.options.classNames.expanded);
        },
        { once: true }
      );
    } else {
      this.el.classList.remove(this.options.classNames.expanded);
      this.setMaxHeight(this.lineHeight * this.options.numLines);
    }
  }

  #getNumLinesFromClassModifier() {
    const numModifierRegex = /--(\w\w)?-?(\d+)$/;
    const classList = [...this.el.classList];
    const numLinesClass = classList.find((cls) => numModifierRegex.test(cls)) || '';
    return Number(numLinesClass.match(numModifierRegex)?.[2] || defaultsOptions.numLines);
  }

  #calcLineHeight() {
    const lhRegex = /^(\d+)(\w+)?$/;
    const currStyle = window.getComputedStyle(this.body);
    const lineHeight = currStyle.getPropertyValue('line-height').match(lhRegex);
    this.lineHeight = parseInt(lineHeight[1], 10);
  }

  setMaxHeight(height) {
    this.body.style.maxHeight = height !== null ? `${height}px` : null;
  }

  #clickTrigger(evt) {
    evt.preventDefault();
    if (this.expanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  setClamping() {
    /* if (this.entityType === 'text') {
      this.clamping = this.getTextClampMaxHeight() < this.body.scrollHeight;
    } else if (this.items.length <= this.options.numLines) {
      this.clamping = false;
    } */
    this.clamping = this.lineHeight * this.options.numLines < this.body.scrollHeight;
  }

  checkClamping() {
    this.setClamping();

    if (this.clamping) {
      this.el.classList.add(this.options.classNames.modifierTruncated);
    } else {
      this.el.classList.remove(this.options.classNames.modifierTruncated);
    }
  }

  #checkBreakPoint() {
    const stateBreakpoint = this.el?.getAttribute('data-expanded');
    if (!stateBreakpoint) return;

    if (stateBreakpoint === 'all') {
      this.expand(false);
    } else if (window.matchMedia(`(min-width: ${BREAKPOINTS[stateBreakpoint]}px)`).matches) {
      if (this.#currentBreakpoint !== stateBreakpoint) {
        this.#currentBreakpoint = stateBreakpoint;
        this.expand(false);
      }
    } else if (this.#currentBreakpoint === stateBreakpoint) {
      this.#currentBreakpoint = null;
      this.collapse(false);
    }
  }
}
